<!--
     <PDFViewer
       
     />

     Accepted props:
      - url
      - pdf_data
      - currentPage
      - numPages
      - scale
      - fit
      - inputPage
      - numberPage
      - iluminateCurrent
      - paginate
     
     Events:
      - changeCurrentPage
      - updatePages
      - updateScale
      - goToPage
-->
<template>

    <div class="pdf-background" ref="pdfContent" v-scroll.self="onScroll">
      <span v-for="i in pages" :key="i" class="pdf-container" @click="pageClick(i)">
        <div
          class="pdf-page" :class="{'iluminate': iluminateCurrent, 'active': i === currentPage}"
          :ref="'page'+i"
          v-if="pdf"
        >
          <div v-if="renderPages[i-1]">
            <PDFPage
              :ref="'canvas'+i"
              :pdf="pdf"
              :page="renderPages[i-1]"
              :scale="scale"
              :pageNumber="i"
              @page-rendered="savePageOffset"
              @isFocus="savePageOffset"
            ></PDFPage>
            </div>
            <div 
              class="pdf-page-count" 
              v-if="numberPage"
            >
            <span :class="is_mobile ? 'pdf_page_count_mobile' : ''">
              {{ i }} / {{ numPages }}
            </span>
          </div>
        </div>
      </span>
    </div>
  </template>
  <script>
  import { mapGetters } from 'vuex'
  import pdfjs from 'pdfjs-dist/webpack'
  import debounce from 'lodash/debounce';
  import PDFPage from '@/components/pdf_viewer/new_components/PDFPage'

  export default {
    name: 'PDFDocument',
    components: {
    pdfjs,
    PDFPage,
    },
    props: {
      is_mobile: {
        type: Boolean,
        default: false,
      },
      url: {
        type: String,
      },
      pdf_data:{
        type: ArrayBuffer
      },
      currentPage: {
        type: Number,
        default: 0
      },
      numPages: {
        type: Number,
        default: 0
      },
      scale: {
        type: Number,
        default: 1
      },
      fit: {
        type: String,
        default: ''
      },
      inputPage: {
        type: Number,
        required: false,
        default: 0
      },
      numberPage: {
        type: Boolean,
        default: false
      },
      iluminateCurrent: {
        type: Boolean,
        default: false
      },
    },
    
    data() {
      return {
        documentError: null,
        last_page_focus: 0,
        loading: false,
        page_wait: 0,
        pdf: null,
        renderTask: null,
        renderPages: [],
        lastLoaded: 0,
        loadingPages: false,
        isFocus: 0,
        pagesOffset: [],
        canScroll: true,
        savePosition: {},
        scroll: 0,
        scrollHeight: 0,
      };
    },
  
    computed: {
        pages() {
          return this.numPages > 0 ? 
                  [...Array(this.numPages).keys()].map(p => p+1) : 
                  [1];
        },
    },
    beforeMount() {
      if(this.url){
        this.getDocument(this.url);
      } else if (this.pdf_data) {
        this.getDocument({data: this.pdf_data});
      }
    },

    mounted() {
      this.goToPage(1);
    },

    methods: {
        savePageOffset(page) {
          const pageRef = this.$refs['page'+page][0];
          this.pagesOffset[page - 1] = pageRef.offsetTop;
        },
        getDocument(url) {
          this.loadingPdf(true);
          pdfjs.getDocument(url).promise
            .then((pdf) => {
              this.pdf = pdf;
              this.updatePages(pdf.numPages);
              this.fetchPages(pdf.numPages);
              this.loadingPdf(false);
              })
            .catch(response => {
              this.$emit('document-errored', {text: 'Failed to retrieve PDF', response});
              console.log('Failed to retrieve PDF', response);
            });
        },
        getPages(pdf, first, last) {
          const allPages = [...Array(last).keys()].map(p => p+1).map(number => pdf.getPage(number));
          return Promise.all(allPages);
        },
        fetchPages(final){
          this.getPages(this.pdf, 1, final)
        .then((pages) => {
          this.renderPages = [...pages];})
        },
        fitDocument() {
          const somePage = this.$refs['page'+this.currentPage][0];
          const currentHeight = somePage.clientHeight;
          const currentWidth = somePage.clientWidth + 2;
          
          const currentSpace = this.$refs['pdfContent'];
          if(!currentSpace) return;
          let localScale = this.scale;
          if (this.fit === 'height') {
            localScale = this.scale * currentSpace.clientHeight / currentHeight;
          } else if (this.fit === 'width' || this.fit === 'overflow' && currentWidth > currentSpace.clientWidth) {
            localScale = this.scale * currentSpace.clientWidth / currentWidth;
          }
          
          if (localScale !== this.scale) {
            this.updateScale(localScale);
          }
        },
        goToPage(page) {
          if(page && page > 0 && page <= this.numPages){
            const toPage = this.$refs['page'+page];
            if (toPage && toPage[0]) {
              this.scrollTo(toPage[0].offsetTop);
            } else if (this.pagesOffset.length >= page && this.pagesOffset[page - 1]) {
              this.scrollTo(this.pagesOffset[page - 1]);
            }
            this.currentPageChanged(page);
          }
        },
        scrollTo(pos) {
          this.canScroll = false;
          if(pos && pos > 0) {
            this.$refs['pdfContent'].scrollTo({top: pos, left: 0, behavior: "auto"});
          }
        },
        onScroll (scroll) {
          const currentScroll = scroll.target.scrollTop;
          
          this.scroll = currentScroll;
          this.scrollHeight = scroll.target.scrollHeight;

          if(this.canScroll){
            
            const currentHeight = this.$refs['pdfContent'].clientHeight;
            const nextPage = this.pagesOffset.findLastIndex(v => v < currentScroll + currentHeight/2);
            let newPage = this.currentPage;

            if (nextPage < 0) {
              newPage = 1;
            } else if (currentScroll < this.pagesOffset[nextPage] - currentHeight/2) {
              newPage = nextPage + 1;
            } else {
              newPage = nextPage + 1;
            }
              
            if (newPage !== this.currentPage) {
              this.currentPageChanged(newPage);
            }
          }
        },

        updatePages(nPages) {
          if (nPages && this.numPages <= 0 && nPages != this.numPages) {
            this.$emit('updatePages',nPages);
          }
        },
        currentPageChanged (newPage) {
          this.$emit('changeCurrentPage',newPage);
        },
        updateScale (scale) {
          this.$emit('updateScale',scale);
        },
        pageClick(page) {
            this.$emit('goToPage', page);
        },
        loadingPdf(loading) {
          this.$emit('isLoading',loading)
        },
        scaleChanged() {
          this.scrollTo(this.savePosition.percent*this.scrollHeight);
          this.$refs['canvas'+this.savePosition.page][0].isFocus();
        },
        stopScroll: debounce(function(){
          this.canScroll = true;
        },100),
    },
    watch: {
        inputPage(input) {
          if (input > 0 && input <= this.numPages) {
            this.canScroll = false;
            this.goToPage(input);
          }
        },
        fit(fit) {
          if (fit) {
            this.fitDocument();
          }
        },
        scale() {
          this.savePosition = {
            page: this.currentPage,
            percent: this.scroll > 0 ? this.scroll/this.scrollHeight : 0
          };
          this.pagesOffset = [];
          this.scrollTo(this.scroll-1)
        },
        scrollHeight(newValue, oldValue) {
          if (oldValue > 0) {
            this.scaleChanged();
          }
        },
        scroll(){
          if(!this.canScroll){
            this.stopScroll();
          }
        },
    },
        
    }
  </script>
<style scoped lang="scss">
  @import "@/sass/pdf.scss";
  .pdf_page_count_mobile{
    font-size: 4vw;
  }
</style>